import { match } from 'ts-pattern';

import { ComponentProps, forwardRef } from 'react';

import { Button, ButtonLink, Card, ListLayout, LoadingButton, Tooltip } from '@ingrid/luterkort-react';
import { ShippingLogo } from '@ingrid/react-shipping-logo';

import { ClearConfirmationModal } from '~src/components/ClearConfirmationModal/ClearConfirmationModal';
import { ConditionalWrapper } from '~src/components/ConditionalWrapper/ConditionalWrapper';
import { LocalizedDate } from '~src/i18n/localized-date';
import { useTranslation } from '~src/i18n/use-translation';

import { useShipmentFormContext } from '../types';
import { CarrierProductAddons, formatAddonTitle } from './carrier-product-addons';
import { CarrierProductPickupPoint } from './carrier-product-pickup-point';
import { CarrierProductSelect, CarrierProductSelectProps } from './carrier-product-select';

interface SelectPickupPointTriggerProps {
  isLoading?: boolean;
}

const SelectPickupPointTrigger = forwardRef<HTMLButtonElement, SelectPickupPointTriggerProps>(
  function SelectTrigger(props, ref) {
    const { isLoading, ...rest } = props;

    const { watch } = useShipmentFormContext();
    const { t } = useTranslation();

    const tooltip = match({
      hasAddresses: !!watch('customer_info.contact.address') && !!watch('contact_from.address'),
      hasDeliveryTime: !!watch('delivery_time.start') && watch('delivery_time.end'),
      isLoading,
    })
      .with({ isLoading: true }, () => null)
      .with({ hasAddresses: false }, () => t('shipments.pickupPoint.selectPickupPointDisabledNoAddress'))
      .with({ hasDeliveryTime: false }, () => t('shipments.pickupPoint.selectPickupPointDisabledNoDeliveryTime'))
      .otherwise(() => null);

    return (
      <ConditionalWrapper
        wrapper={c => (
          <Tooltip content={tooltip}>
            <span>{c}</span>
          </Tooltip>
        )}
        when={!!tooltip}
      >
        {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
        <LoadingButton variant="secondary" loading={isLoading} disabled={isLoading || !!tooltip} ref={ref} {...rest}>
          {t('shipments.pickupPoint.selectPickupPoint')}
        </LoadingButton>
      </ConditionalWrapper>
    );
  }
);

function AddonCard() {
  const { watch } = useShipmentFormContext();
  const { t } = useTranslation();

  const addons = watch('addons');

  if (!addons?.length) {
    return false;
  }

  return (
    <Card.Content>
      <ListLayout.Stack>
        <ListLayout.SecondaryText>{t('common.addons')}</ListLayout.SecondaryText>
        <ListLayout.SubRow gap="lg">
          {addons.map(addon => (
            <ListLayout.PrimaryText key={addon.code}>{formatAddonTitle(addon)}</ListLayout.PrimaryText>
          ))}
        </ListLayout.SubRow>
      </ListLayout.Stack>
    </Card.Content>
  );
}

function PickupPointCard() {
  const { watch, setValue } = useShipmentFormContext();

  const locationRef = watch('location_ref');
  const method = watch('shipping_method');
  const meta = watch('meta');

  const { t } = useTranslation();

  const pickupPointDialogProps: Omit<ComponentProps<typeof CarrierProductPickupPoint>, 'trigger'> = {
    value: {
      location_ref: locationRef,
    },
    onSubmit: location => {
      setValue('location_type', location.location_type);
      setValue('location_ref', location.location_ref);
      setValue('contact_to.address', location.address);
      setValue('meta', { ...meta, ...location.meta });
    },
  };

  function handleClear() {
    setValue('location_type', undefined);
    setValue('location_ref', undefined);
    setValue('contact_to', undefined);
  }

  if (!method?.delivery_types?.includes('PICKUP')) {
    return false;
  }

  return (
    <Card.Content>
      <ListLayout.Column>
        <ListLayout.Row>
          {locationRef ? (
            <ListLayout.Stack>
              <ListLayout.SecondaryText>{t('common.pickupPoint')}</ListLayout.SecondaryText>
              <ListLayout.PrimaryText>{locationRef}</ListLayout.PrimaryText>
            </ListLayout.Stack>
          ) : (
            <CarrierProductPickupPoint
              trigger={query => <SelectPickupPointTrigger isLoading={query.isLoading} />}
              {...pickupPointDialogProps}
            />
          )}

          {locationRef && (
            <ListLayout.Actions>
              <CarrierProductPickupPoint
                trigger={() => <ButtonLink>{t('common.edit')}</ButtonLink>}
                {...pickupPointDialogProps}
              />

              <ClearConfirmationModal clearedItemName={`common.pickupPoint`} onSubmit={handleClear} />
            </ListLayout.Actions>
          )}
        </ListLayout.Row>
      </ListLayout.Column>
    </Card.Content>
  );
}

export function CarrierProduct() {
  const { watch, setValue } = useShipmentFormContext();
  const { t } = useTranslation();

  const [shipping_method, shipping_date, delivery_time, addons] = watch([
    'shipping_method',
    'shipping_date',
    'delivery_time',
    'addons',
  ]);

  const selectProps: Omit<CarrierProductSelectProps, 'trigger'> = {
    mode: 'add',
    value: {
      shipping_method,
      shipping_date,
      delivery_time,
    },
    onSubmit: values => {
      if (shipping_method?.id && shipping_method?.id !== values.shipping_method?.id) {
        // Reset pickup point if shipping method is changed
        setValue('location_type', undefined);
        setValue('location_ref', undefined);
        setValue('addons', undefined);
      }

      setValue('shipping_method', values.shipping_method, { shouldDirty: true });
      setValue('shipping_date', values.shipping_date, { shouldDirty: true });
      setValue('delivery_time', values.delivery_time, { shouldDirty: true });
    },
  };

  return (
    <Card.List>
      <Card.Wrapper as="section">
        <Card.Header>{t('common.carrierProduct')}</Card.Header>
        <Card.Content>
          {shipping_method ? (
            <ListLayout.Column>
              <ListLayout.Row>
                <ShippingLogo method={shipping_method.id} width="40" />

                <ListLayout.Stack>
                  <ListLayout.SecondaryText>{shipping_method.carrier}</ListLayout.SecondaryText>
                  <ListLayout.PrimaryText>{shipping_method.name}</ListLayout.PrimaryText>
                </ListLayout.Stack>

                <ListLayout.Actions>
                  <CarrierProductAddons
                    trigger={({ disabled }) => <ButtonLink disabled={disabled}>{t('common.addons')}</ButtonLink>}
                    onSubmit={addons => setValue('addons', addons, { shouldDirty: true })}
                    value={addons}
                  />

                  <CarrierProductSelect
                    trigger={<ButtonLink>{t('common.edit')}</ButtonLink>}
                    {...selectProps}
                    mode="update"
                  />

                  <ClearConfirmationModal
                    clearedItemName={`common.carrierProduct`}
                    onSubmit={() => {
                      setValue('shipping_method', undefined);
                      setValue('shipping_date', undefined);
                      setValue('delivery_time', undefined);
                      setValue('addons', undefined);
                    }}
                  />
                </ListLayout.Actions>
              </ListLayout.Row>

              <ListLayout.Row>
                <ListLayout.Stack>
                  <ListLayout.SecondaryText>{t('common.shippingDate')}</ListLayout.SecondaryText>
                  <ListLayout.PrimaryText>
                    <LocalizedDate
                      options={{ weekday: 'long', month: 'long', year: undefined }}
                      dateTime={shipping_date!}
                    />
                  </ListLayout.PrimaryText>
                </ListLayout.Stack>
              </ListLayout.Row>

              {delivery_time && (
                <ListLayout.Row>
                  <ListLayout.Stack>
                    <ListLayout.SecondaryText>{t('common.deliveryTime')}</ListLayout.SecondaryText>
                    <ListLayout.PrimaryText>
                      <LocalizedDate
                        options={{ weekday: 'long', month: 'long', year: undefined }}
                        // eslint-disable-next-line @typescript-eslint/non-nullable-type-assertion-style
                        dateTime={delivery_time?.start as string}
                        inline
                        time
                      />{' '}
                      -{' '}
                      <LocalizedDate
                        options={{ weekday: 'long', month: 'long', year: undefined }}
                        // eslint-disable-next-line @typescript-eslint/non-nullable-type-assertion-style
                        dateTime={delivery_time?.end as string}
                        inline
                        time
                      />
                    </ListLayout.PrimaryText>
                  </ListLayout.Stack>
                </ListLayout.Row>
              )}
            </ListLayout.Column>
          ) : (
            <CarrierProductSelect
              {...selectProps}
              trigger={<Button variant="secondary">{t('common.selectCarrierProduct')}</Button>}
            />
          )}
        </Card.Content>

        <PickupPointCard />
        <AddonCard />
      </Card.Wrapper>
    </Card.List>
  );
}
